/* eslint-disable i18next/no-literal-string */
import { ComponentsStyles, HighContrastSelector, IButtonStyles, IModalStyles, IPalette } from '@fluentui/react';
import { pageDataService } from 'common/utilities';
import { mergeWith } from 'lodash';

import { AppSemanticColors } from './AppSemanticColors';

const isWebView = pageDataService.isWebView();

export const contextualMenuBorderRadius = {
  borderRadius: 12,
};

const labelLineHeight = 20;
const buttonBorderRadius = 16;
const mobileButtonBorderRadius = '24px';
const mobileButtonPadding = '9px 17px';

const getDefaultButtonStyles = (borderRadius = buttonBorderRadius): IButtonStyles => ({
  root: {
    border: 'none',
    borderRadius,
    padding: '5px 17px',
    minHeight: 32,
    height: 'auto',
    // a11y - avoid maxHeight so content isn't clipped when zoomed + font size increased:
    // maxHeight: 96,
    overflow: 'hidden',
    minWidth: 'auto',
    boxSizing: 'border-box',
    '&::after': {
      outline: 'none!important',
      borderRadius: buttonBorderRadius,
      '@media only screen and (max-width: 480px)': {
        borderRadius: mobileButtonBorderRadius,
      },
    },
    '@media only screen and (max-width: 480px)': {
      minHeight: 42,
      borderRadius: mobileButtonBorderRadius,
    },
    svg: {
      flexShrink: 0,
    },
  },
  rootPressed: {
    border: 'none',
  },
  rootHovered: {
    border: 'none',
  },
  rootDisabled: {
    border: 'none',
  },
  label: {
    position: 'static',
    lineHeight: labelLineHeight,
    minHeight: labelLineHeight,
    // a11y - avoid maxHeight so content isn't clipped when zoomed + font size increased:
    // maxHeight: labelLineHeight * 3,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    overflow: 'hidden',
    fontWeight: 600,
    fontSize: '1.4rem',
    textAlign: 'center',
    padding: 0,
    margin: 0,
  },
  textContainer: {
    paddingLeft: 5,
    paddingRight: 6,
    margin: 0,
  },
  icon: {
    fontWeight: '600 !important',
    margin: '0px 1px 0px 0px',
    padding: 0,
  },
  menuIcon: {
    paddingLeft: 5,
    paddingRight: 6,
    margin: 0,
  },
});

// TODO: figure out how to generate media queries from responsive breakpoints.
const defaultComponentsStyles = (palette: Partial<IPalette>, appColors: AppSemanticColors): Partial<ComponentsStyles> => ({
  ContextualMenu: {
    styles: {
      root: {
        // The Drawer breakpoint is <=640
        '@media only screen and (min-width: 641px)': {
          ...contextualMenuBorderRadius,
          selectors: {
            '.ms-ContextualMenu-item:first-of-type': {
              'button, button:after': {
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
              },
            },
            '.ms-ContextualMenu-item:last-of-type': {
              'button, button:after': {
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
              },
            },
          },
        },
      },
      subComponentStyles: {
        callout: {
          calloutMain: contextualMenuBorderRadius,
          beakCurtain: contextualMenuBorderRadius,
          root: contextualMenuBorderRadius,
        },
        // Allow menu items to wrap & grow to multiple lines for accessibility
        menuItem: {
          root: {
            height: 'auto',
            minHeight: '36px',
          },
        },
      },
    },
  },
  Overlay: {
    styles: {
      root: {
        backgroundColor: appColors.dialogOverlayBackground,
      },
    },
  },
  Modal: {
    styles: {
      main: {
        background: appColors.dialogBackground,
        [HighContrastSelector]: {
          background: `${palette.white} !important`,
        },
        width: isWebView ? '100%' : '740px',
        minWidth: isWebView ? '100%' : '740px',
        maxWidth: isWebView ? '100%' : '740px',
        height: isWebView ? '100%' : '560px',
        ...(isWebView && { maxHeight: '100%' }),
        '@media only screen and (max-width: 640px)': {
          position: 'fixed',
          top: 0,
          bottom: 0,
          height: 'auto',
          maxHeight: '100%',
          width: '100vw',
          minWidth: '100vw',
          maxWidth: '100vw',
        },
        '@media only screen  and (min-width: 641px) and (max-width:1024px)': {
          width: isWebView ? '100%' : '640px',
          minWidth: isWebView ? '100%' : '640px',
          maxWidth: isWebView ? '100%' : '640px',
        },
      },
      root: {
        background: appColors.dialogBackdrop,
        mixBlendMode: 'normal',
      },
      scrollableContent: {
        height: '100%',
        maxHeight: '100% !important',
      },
    } as Partial<IModalStyles>,
  },
  PrimaryButton: {
    styles: mergeWith(getDefaultButtonStyles(), {
      root: {
        '@media only screen and (max-width: 480px)': {
          padding: mobileButtonPadding,
        },
      },
    }),
  },
  DefaultButton: {
    styles: mergeWith(getDefaultButtonStyles(), {
      root: {
        '@media only screen and (max-width: 480px)': {
          padding: mobileButtonPadding,
        },
      },
    }),
  },
  PillButton: {
    styles: mergeWith(getDefaultButtonStyles(), {
      root: {
        '@media only screen and (max-width: 480px)': {
          padding: mobileButtonPadding,
        },
      },
    }),
  },
  ActionButton: {
    styles: mergeWith(getDefaultButtonStyles(0), {
      root: { padding: 0, margin: 0 },
      rootFocused: {
        padding: 0,
      },
      rootPressed: {
        padding: 0,
      },
    }),
  },
  Link: {
    styles: {
      root: {
        '&:focus': {
          textDecoration: 'underline',
        },
      },
    },
  },
  Checkbox: {
    styles: {
      checkbox: {
        borderRadius: '4px',
      },
    },
  },
});

export function createAppFluentComponentsStyles(
  styledComponents: Partial<ComponentsStyles>,
  palette: Partial<IPalette>,
  appColors: AppSemanticColors
): ComponentsStyles {
  return mergeWith(defaultComponentsStyles(palette, appColors), styledComponents);
}
